import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationControllerService, UserDTO } from '@chicilon/angular-user-service';
import { ChangePasswordBody } from '@core/models/account-setting';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FormControlComponent } from '@shared/components/form-control/form-control.component';
import { TOAST_KEYS } from '@shared/constants/toast-keys';
import { PasswordMatchValidator } from '@shared/validators/password-match.validator';
import { passwordRulesValidator } from '@shared/validators/password-rules-validator';
import { AppState } from '@state/app.state';
import { selectUserInfo } from '@state/auth/auth.selectors';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-change-password-dialog',
  imports: [
    DialogModule,
    ButtonComponent,
    TranslateModule,
    CommonModule,
    FormControlComponent,
    PasswordModule,
    ReactiveFormsModule
  ],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss'
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
  @Input() isChangePassword = false;
  @Output() isChangePasswordChange = new EventEmitter<boolean>();
  readonly translatePrefix = 'USER-MANAGEMENT.SETTINGS.';
  readonly unsubscribe$ = new Subject();
  changePasswordForm: FormGroup = new FormGroup({});
  currentUser: UserDTO | undefined;

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private authenticationControllerService: AuthenticationControllerService,
    private messageService: MessageService,
    private store: Store<AppState>
  ) {}

  get passwordControl() {
    return this.changePasswordForm.controls['password'];
  }

  get isInvalidForm() {
    return this.changePasswordForm.invalid;
  }

  ngOnInit(): void {
    this.store
      .select(selectUserInfo)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(user => {
        this.currentUser = user;
      });

    this.changePasswordForm = this.fb.group(
      {
        currentPassword: this.fb.control('', [Validators.required]),
        password: this.fb.control('', [Validators.required, passwordRulesValidator()]),
        confirmPassword: this.fb.control('', [Validators.required])
      },
      { validators: PasswordMatchValidator }
    );
  }

  onChangePassword() {
    if (this.isInvalidForm || !this.currentUser?.id) return;
    const valueForm = this.changePasswordForm.value;

    const body: ChangePasswordBody = {
      old_password: valueForm.currentPassword!,
      new_password: valueForm.password!
    };
    this.authenticationControllerService
      .changePassword(body, this.currentUser.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.messageService.add({
            key: TOAST_KEYS.G_MESSAGE,
            severity: 'success',
            detail: this.translateService.instant(this.translatePrefix + 'MESSAGE.CHANGE-PASSWORD-SUCCESS')
          });
          this.isChangePassword = false;
          this.changePasswordForm.reset();
        },
        error: err => {
          this.messageService.add({
            key: TOAST_KEYS.G_MESSAGE,
            severity: 'error',
            detail: err?.error?.error?.message
          });
        }
      });
  }

  onCancelChangePassword() {
    this.isChangePassword = false;
    this.changePasswordForm.reset();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
